import Logo from "@/components/Logo";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, Check, AlertTriangle, X, GraduationCap, ClipboardList } from "lucide-react";
import { mentorMediaUrl } from "@/components/mentor/UploadZone";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  const d = await r.json().catch(() => null);
  if (!r.ok) throw new Error((d && d.detail) || "Request failed");
  return d;
};

function SkillBars({ skills }) {
  if (!skills?.length) return <div className="text-[12px] text-zinc-500 font-light">No reviewed submissions yet.</div>;
  return (
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
      {skills.map((c) => (
        <div key={c.key}>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-zinc-200">{c.label}</span>
            <span className="text-zinc-400">{c.score}% · {c.total}</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-white/8">
            <div className={`h-1.5 rounded-full ${c.score >= 70 ? "bg-[#7ee0a1]" : c.score >= 40 ? "bg-[#e6c67a]" : "bg-[#ff7a7a]"}`} style={{ width: `${c.score}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function FeedbackForm({ studentId, lessons, exercises, onSent }) {
  const [text, setText] = useState("");
  const [lessonId, setLessonId] = useState("");
  const [exerciseId, setExerciseId] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return toast.error("Write some feedback first.");
    setBusy(true);
    try {
      await api("/mentor/coach/feedback", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: studentId, text, lesson_id: lessonId || null, exercise_id: exerciseId || null }),
      });
      toast.success("Feedback sent to student");
      setText(""); setLessonId(""); setExerciseId("");
      onSent();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const sel = "bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-xs text-zinc-200";
  return (
    <div className="glass-card glass-card-featured p-4 space-y-3" data-testid="coach-feedback-form">
      <div className="section-label text-[10px] text-[#e6c67a]">Send feedback</div>
      <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Comment, correct or encourage — the student sees this in Chart Mentor…"
        className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2 text-sm text-zinc-200 resize-none focus:outline-none focus:border-[#e6c67a]/40"
        data-testid="coach-feedback-text" />
      <div className="flex flex-wrap gap-2">
        <select value={lessonId} onChange={(e) => setLessonId(e.target.value)} className={sel} data-testid="coach-assign-lesson">
          <option value="" className="bg-[#0A0A0A]">Assign lesson (optional)</option>
          {lessons.map((l) => <option key={l.id} value={l.id} className="bg-[#0A0A0A]">{l.title}</option>)}
        </select>
        <select value={exerciseId} onChange={(e) => setExerciseId(e.target.value)} className={sel} data-testid="coach-assign-exercise">
          <option value="" className="bg-[#0A0A0A]">Assign markup exercise (optional)</option>
          {exercises.map((ex) => <option key={ex.id} value={ex.id} className="bg-[#0A0A0A]">{ex.title}</option>)}
        </select>
        <button onClick={send} disabled={busy} className="btn-solid px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 ml-auto disabled:opacity-50" data-testid="coach-feedback-send">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
        </button>
      </div>
    </div>
  );
}

const V = { correct: ["text-[#7ee0a1]", Check], review: ["text-[#e6c67a]", AlertTriangle], incorrect: ["text-[#ff7a7a]", X] };

function StudentDetail({ studentId, lessons }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api(`/mentor/coach/students/${studentId}`).then(setD).catch((e) => toast.error(e.message));
  }, [studentId]);
  useEffect(() => { setD(null); load(); }, [load]);
  const [exercises, setExercises] = useState([]);
  useEffect(() => {
    fetch(`${API}/practice/exercises`, { credentials: "include" }).then((r) => r.json()).then(setExercises).catch(() => {});
  }, []);
  if (!d) return <div className="py-16 text-center section-label text-[11px] text-zinc-600 animate-pulse">Loading student…</div>;
  return (
    <div className="space-y-5" data-testid="coach-student-detail">
      <div>
        <h2 className="font-heading text-2xl font-light">{d.user.name}</h2>
        <div className="text-[12px] text-zinc-500">{d.user.email} · {d.trainer.xp} XP · {d.trainer.completed} lessons completed</div>
      </div>
      <FeedbackForm studentId={studentId} lessons={lessons} exercises={exercises} onSent={load} />
      <div className="glass-card p-4">
        <div className="section-label text-[10px] text-zinc-400 mb-3">Skill scores</div>
        <SkillBars skills={d.skills} />
      </div>
      {d.reviews.length > 0 && (
        <div className="glass-card p-4">
          <div className="section-label text-[10px] text-zinc-400 mb-3">Markup reviews</div>
          <div className="space-y-4">
            {d.reviews.map((r) => (
              <div key={r.id} className="flex gap-3" data-testid={`coach-review-${r.id}`}>
                <img src={mentorMediaUrl(r.image_path)} alt="" className="h-16 w-24 object-cover rounded border border-white/10 cursor-pointer"
                  onClick={() => window.open(mentorMediaUrl(r.image_path), "_blank")} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-zinc-200">{r.market} {r.timeframe} · {new Date(r.created_at).toLocaleDateString()}</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {r.categories.map((c) => {
                      const [cls, Icon] = V[c.verdict] || ["text-zinc-600", X];
                      return c.verdict === "not_marked" ? null : (
                        <span key={c.key} className={`inline-flex items-center gap-1 text-[10px] ${cls}`}><Icon className="h-3 w-3" />{c.label}</span>
                      );
                    })}
                  </div>
                  <div className="mt-1 text-[11px] text-zinc-500 font-light line-clamp-2">{r.summary}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {d.journal.length > 0 && (
        <div className="glass-card p-4">
          <div className="section-label text-[10px] text-zinc-400 mb-3">AI-reviewed journal trades</div>
          <div className="space-y-3">
            {d.journal.map((t) => (
              <div key={t.id} className="text-[12px]">
                <span className="text-zinc-200 font-medium">{t.symbol}</span>
                <span className={`ml-2 ${t.result === "win" ? "text-[#7ee0a1]" : t.result === "loss" ? "text-[#ff7a7a]" : "text-zinc-400"}`}>{t.result}</span>
                <span className="ml-2 text-zinc-500">plan: {t.ai_review?.followed_plan}</span>
                <span className="ml-2 text-zinc-500">{(t.ai_review?.scores || []).map((s) => `${s.label.split(" ")[0]} ${s.score}`).join(" · ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {d.exercises.length > 0 && (
        <div className="glass-card p-4">
          <div className="section-label text-[10px] text-zinc-400 mb-3">Practice exercises</div>
          {d.exercises.map((e) => (
            <div key={e.id} className="text-[12px] text-zinc-300 flex items-center gap-2 py-0.5">
              <ClipboardList className="h-3.5 w-3.5 text-zinc-500" /> {e.exercise_title}
              <span className={e.passed ? "text-[#7ee0a1]" : "text-[#e6c67a]"}>{e.score}/10</span>
            </div>
          ))}
        </div>
      )}
      {d.feedback.length > 0 && (
        <div className="glass-card p-4">
          <div className="section-label text-[10px] text-zinc-400 mb-3">Feedback history</div>
          {d.feedback.map((fb) => (
            <div key={fb.id} className="text-[12px] text-zinc-400 font-light py-1 border-b border-white/5">
              <span className="text-zinc-200">{fb.coach_name}:</span> {fb.text}
              {fb.lesson_title && <span className="text-[#9fd3ff]"> → {fb.lesson_title}</span>}
              {fb.exercise_title && <span className="text-[#e6c67a]"> → {fb.exercise_title}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CoachDesk() {
  const [students, setStudents] = useState(null);
  const [selected, setSelected] = useState(null);
  const [lessons, setLessons] = useState([]);
  useEffect(() => {
    api("/mentor/coach/students").then(setStudents).catch((e) => toast.error(e.message));
    api("/mentor/coach/lessons").then(setLessons).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
            <span className="text-zinc-700">/</span>
            <span className="section-label text-[10px] text-[#e6c67a]">Coach Desk</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/mentor" className="text-zinc-400 hover:text-white transition-colors">Chart Mentor</Link>
            <Link to="/community" className="text-zinc-400 hover:text-white transition-colors">Community</Link>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="coach-desk-page">
        <div className="section-label text-[10px] text-[#e6c67a]">MBM Coach Review Desk</div>
        <h1 className="mt-2 font-heading text-3xl font-light tracking-tight">Every student submission, in one place.</h1>
        <div className="mt-8 grid lg:grid-cols-[320px_1fr] gap-6">
          <div className="space-y-2" data-testid="coach-student-list">
            {students === null && <div className="section-label text-[11px] text-zinc-600 animate-pulse">Loading students…</div>}
            {students?.length === 0 && <div className="glass-card p-5 text-sm text-zinc-500 font-light">No student submissions yet.</div>}
            {students?.map((s) => (
              <button key={s.id} onClick={() => setSelected(s.id)}
                className={`w-full glass-card p-4 text-left transition-colors ${selected === s.id ? "border-[#e6c67a]/40" : "hover:border-white/25"}`}
                data-testid={`coach-student-${s.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-100 font-medium truncate">{s.name}</span>
                  {s.avg_score != null && (
                    <span className={`text-[12px] ${s.avg_score >= 70 ? "text-[#7ee0a1]" : s.avg_score >= 40 ? "text-[#e6c67a]" : "text-[#ff7a7a]"}`}>{s.avg_score}%</span>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-zinc-500">{s.reviews} reviews · {s.setups} setups · {s.exercises} exercises · {s.journal_reviews} journal</div>
                {s.weak.length > 0 && <div className="mt-1 text-[10px] text-[#ff9d5c]">Weak: {s.weak.join(", ")}</div>}
              </button>
            ))}
          </div>
          <div>
            {selected ? <StudentDetail studentId={selected} lessons={lessons} />
              : <div className="glass-card p-10 text-center text-sm text-zinc-500 font-light flex flex-col items-center gap-3">
                  <GraduationCap className="h-6 w-6 text-zinc-600" /> Select a student to review their markups, journal and skill scores.
                </div>}
          </div>
        </div>
      </main>
    </div>
  );
}
